/**
 * 视频缓存服务
 * 支持完整视频缓存和部分播放缓存
 * 使用IndexedDB存储视频数据，支持断点续传和离线播放
 */

const DB_NAME = 'music-cache-db';
const DB_VERSION = 9; // 🔥 升级到 9：新增 audio-ranges / audio-metadata（音频 Range 缓存）
const VIDEO_CACHE_STORE = 'video-files';
const PARTIAL_CACHE_STORE = 'partial-video-cache';

class VideoCacheService {
  constructor() {
    this.db = null;
    this.isInitialized = false;
    this.initPromise = null;
    this.initDB();
  }

  /**
   * 初始化IndexedDB数据库
   */
  async initDB() {
    // 如果已经在初始化中，返回现有的Promise
    if (this.initPromise) {
      return this.initPromise;
    }
    
    this.initPromise = this._doInitDB();
    return this.initPromise;
  }
  
  async _doInitDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onupgradeneeded = (event) => {
        const db = request.result;
        console.log('🎬 [视频缓存] 数据库升级中，版本:', event.oldVersion, '->', event.newVersion);
        
        // 🔥 强制创建所有必要的对象存储，确保数据库结构完整
        console.log('🎬 [视频缓存] 当前数据库对象存储:', Array.from(db.objectStoreNames));
        
        // 确保 music-files 对象存储存在（兼容现有音频缓存）
        if (!db.objectStoreNames.contains('music-files')) {
          db.createObjectStore('music-files');
          console.log('🎬 [视频缓存] 创建 music-files 对象存储');
        }
        
        // 确保 meta 对象存储存在（兼容现有音频缓存）
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta');
          console.log('🎬 [视频缓存] 创建 meta 对象存储');
        }

        // 🔥 强制创建完整视频缓存存储 - 移除keyPath配置，使用简单存储
        if (!db.objectStoreNames.contains(VIDEO_CACHE_STORE)) {
          const videoStore = db.createObjectStore(VIDEO_CACHE_STORE);
          videoStore.createIndex('songId', 'songId', { unique: false });
          videoStore.createIndex('userId', 'userId', { unique: false });
          videoStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('🎬 [视频缓存] 创建 video-files 对象存储');
        } else {
          console.log('🎬 [视频缓存] video-files 对象存储已存在');
        }

        // 🔥 强制创建部分视频缓存存储 - 移除keyPath配置，使用简单存储
        if (!db.objectStoreNames.contains(PARTIAL_CACHE_STORE)) {
          const partialStore = db.createObjectStore(PARTIAL_CACHE_STORE);
          partialStore.createIndex('songId', 'songId', { unique: false });
          partialStore.createIndex('userId', 'userId', { unique: false });
          partialStore.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('🎬 [视频缓存] 创建 partial-video-cache 对象存储');
        } else {
          console.log('🎬 [视频缓存] partial-video-cache 对象存储已存在');
        }
        
        // 🔥 新增：确保图集媒体缓存对象存储存在
        if (!db.objectStoreNames.contains('music-album')) {
          db.createObjectStore('music-album');
          console.log('🎬 [视频缓存] 创建 music-album 对象存储');
        }
        
        if (!db.objectStoreNames.contains('music-lrc')) {
          db.createObjectStore('music-lrc');
          console.log('🎬 [视频缓存] 创建 music-lrc 对象存储');
        }
        
        // 🔥 新增（v8）：HLS 分片缓存与分片清单对象存储
        if (!db.objectStoreNames.contains('video-segments')) {
          db.createObjectStore('video-segments');
          console.log('🎬 [视频缓存] 创建 video-segments 对象存储');
        }
        if (!db.objectStoreNames.contains('video-seg-manifest')) {
          db.createObjectStore('video-seg-manifest');
          console.log('🎬 [视频缓存] 创建 video-seg-manifest 对象存储');
        }
        
        // 🔥 新增（v9）：音频 Range 缓存（in-line key）
        if (!db.objectStoreNames.contains('audio-ranges')) {
          const rs = db.createObjectStore('audio-ranges', { keyPath: 'key' });
          rs.createIndex('audioId', 'audioId', { unique: false });
          rs.createIndex('cachedAt', 'cachedAt', { unique: false });
          console.log('🎬 [视频缓存] 创建 audio-ranges 对象存储');
        }
        if (!db.objectStoreNames.contains('audio-metadata')) {
          db.createObjectStore('audio-metadata', { keyPath: 'audioId' });
          console.log('🎬 [视频缓存] 创建 audio-metadata 对象存储');
        }
        
        // 🔥 关键修复：检查现有对象存储的配置，如果不正确则删除重建
        try {
          const videoStore = db.transaction([VIDEO_CACHE_STORE], 'readonly').objectStore(VIDEO_CACHE_STORE);
          console.log('🎬 [视频缓存] video-files 对象存储配置正常');
        } catch (error) {
          console.warn('🎬 [视频缓存] video-files 对象存储配置异常，需要重建:', error.message);
          // 在升级过程中无法删除对象存储，需要用户清除浏览器数据
        }
        
        try {
          const partialStore = db.transaction([PARTIAL_CACHE_STORE], 'readonly').objectStore(PARTIAL_CACHE_STORE);
          console.log('🎬 [视频缓存] partial-video-cache 对象存储配置正常');
        } catch (error) {
          console.warn('🎬 [视频缓存] partial-video-cache 对象存储配置异常，需要重建:', error.message);
          // 在升级过程中无法删除对象存储，需要用户清除浏览器数据
        }
        
        console.log('🎬 [视频缓存] 升级后数据库对象存储:', Array.from(db.objectStoreNames));
        
        // 🔥 强制清除可能存在的旧配置问题
        console.log('🎬 [视频缓存] 数据库升级完成，新配置已生效');
      };
      
      request.onsuccess = (event) => {
        this.db = event.target.result;
        this.isInitialized = true;
        console.log('🎬 [视频缓存] IndexedDB初始化成功，使用统一数据库:', DB_NAME);
        console.log('🎬 [视频缓存] 数据库对象存储:', Array.from(this.db.objectStoreNames));
        resolve(this.db);
      };
      
      request.onerror = (event) => {
        console.error('❌ [视频缓存] IndexedDB初始化失败:', event.target.error);
        this.isInitialized = false;
        reject(event.target.error);
      };
    });
  }
  
  /**
   * 确保数据库已初始化
   */
  async ensureDBReady() {
    if (!this.isInitialized) {
      await this.initDB();
    }
    return this.db;
  }

  /**
   * 原生IndexedDB辅助方法 - 获取数据
   */
  async _dbGet(storeName, key) {
    return new Promise(async (resolve, reject) => {
      try {
        console.log(`🎬 [视频缓存] 开始数据库查询:`, {
          storeName,
          key,
          dbState: this.db ? this.db.readyState : 'null',
          timestamp: new Date().toISOString()
        });

        // 检查数据库连接状态
        if (!this.db || this.db.readyState !== 'open') {
          console.warn('🎬 [视频缓存] 数据库连接已关闭，尝试重新连接...', {
            hasDb: !!this.db,
            readyState: this.db ? this.db.readyState : 'null'
          });
          await this.initDB();
          console.log('🎬 [视频缓存] 数据库重新连接完成');
        }
        
        console.log(`🎬 [视频缓存] 创建数据库事务...`);
        const transaction = this.db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get(key);
        
        request.onsuccess = () => {
          console.log(`🎬 [视频缓存] 数据库查询成功:`, {
            storeName,
            key,
            hasResult: !!request.result,
            resultType: request.result ? typeof request.result : 'undefined'
          });
          resolve(request.result);
        };
        
        request.onerror = () => {
          console.error(`🎬 [视频缓存] 数据库查询失败:`, {
            storeName,
            key,
            error: request.error
          });
          reject(request.error);
        };
      } catch (error) {
        console.error('🎬 [视频缓存] 数据库操作失败:', {
          storeName,
          key,
          error: error.message,
          stack: error.stack
        });
        reject(error);
      }
    });
  }

  /**
   * 原生IndexedDB辅助方法 - 存储数据
   */
  async _dbPut(storeName, data) {
    return new Promise(async (resolve, reject) => {
      try {
        // 检查数据库连接状态
        if (!this.db || this.db.readyState !== 'open') {
          console.warn('🎬 [视频缓存] 数据库连接已关闭，尝试重新连接...');
          await this.initDB();
        }
        
        const transaction = this.db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        
        // 🔥 关键修复：使用cacheKey作为存储键，不依赖keyPath
        let request;
        if (storeName === VIDEO_CACHE_STORE || storeName === PARTIAL_CACHE_STORE) {
          // 使用cacheKey作为存储键
          request = store.put(data, data.cacheKey);
        } else {
          // 其他存储使用默认方式
          request = store.put(data);
        }
        
        request.onsuccess = () => {
          console.log(`🎬 [视频缓存] 数据库存储成功:`, {
            storeName,
            cacheKey: data.cacheKey,
            dataSize: data.videoBlob ? Math.round(data.videoBlob.size / 1024 / 1024) + 'MB' : 'unknown'
          });
          resolve(request.result);
        };
        request.onerror = () => {
          console.error(`🎬 [视频缓存] 数据库存储失败:`, {
            storeName,
            cacheKey: data.cacheKey,
            error: request.error
          });
          reject(request.error);
        };
      } catch (error) {
        console.error('🎬 [视频缓存] 数据库操作失败:', error);
        
        // 🔥 关键修复：检测对象存储配置错误，提供解决方案
        if (error.name === 'DataError' && error.message.includes('out-of-line keys')) {
          console.error('❌ [视频缓存] 对象存储配置错误，尝试自动修复...');
          
          // 尝试自动重置数据库
          try {
            await this.resetDatabase();
            console.log('✅ [视频缓存] 数据库自动重置成功，重试存储操作...');
            
            // 重试存储操作
            const retryRequest = this.db.transaction([storeName], 'readwrite').objectStore(storeName);
            const retryResult = retryRequest.put(data, data.cacheKey);
            
            retryResult.onsuccess = () => {
              console.log('✅ [视频缓存] 重试存储成功');
              resolve(retryResult.result);
            };
            retryResult.onerror = () => {
              console.error('❌ [视频缓存] 重试存储仍然失败:', retryResult.error);
              this.triggerCacheError('数据库配置错误，请清除浏览器缓存后重试');
              reject(retryResult.error);
            };
            return;
          } catch (resetError) {
            console.error('❌ [视频缓存] 自动重置失败:', resetError);
            this.triggerCacheError('对象存储配置错误，请清除浏览器缓存后重试');
          }
        }
        
        reject(error);
      }
    });
  }

  /**
   * 触发缓存错误事件，通知用户
   */
  triggerCacheError(message) {
    // 可以在这里添加用户通知逻辑，比如显示toast或弹窗
    console.error('🎬 [视频缓存] 缓存错误:', message);
    
    // 如果页面有全局错误处理，可以触发自定义事件
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      const errorEvent = new CustomEvent('videoCacheError', {
        detail: { message }
      });
      window.dispatchEvent(errorEvent);
    }
  }

  /**
   * 原生IndexedDB辅助方法 - 删除数据
   */
  async _dbDelete(storeName, key) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 原生IndexedDB辅助方法 - 获取所有数据
   */
  async _dbGetAll(storeName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 原生IndexedDB辅助方法 - 基于索引获取数据
   */
  async _dbGetByIndex(storeName, indexName, value) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(value);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 原生IndexedDB辅助方法 - 获取所有键
   */
  async _dbGetAllKeys(storeName) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAllKeys();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 生成缓存键
   * 统一使用 video 作为视频缓存键，支持完整和部分缓存
   */
  generateCacheKey(userId, songId, type = 'video') {
    return `${userId}_${songId}_${type}`;
  }

  /**
   * 生成旧的缓存键格式（兼容music-files存储）
   */
  generateOldCacheKey(userId, songId) {
    return `${userId}_${songId}_audio`;
  }

  /**
   * 迁移旧的视频缓存到新的存储结构
   */
  async migrateOldVideoCache(userId, songId, videoBlob) {
    try {
      const cacheKey = this.generateCacheKey(userId, songId, 'video');
      
      const cacheData = {
        cacheKey,
        userId,
        songId,
        videoBlob,
        metadata: {
          size: videoBlob.size,
          type: videoBlob.type || 'video/mp4',
          migrated: true
        },
        timestamp: Date.now()
      };
      
      await this._dbPut(VIDEO_CACHE_STORE, cacheData);
      console.log('🔄 [视频缓存] 迁移旧缓存成功:', songId);
    } catch (error) {
      console.error('❌ [视频缓存] 迁移旧缓存失败:', error);
    }
  }

  /**
   * 检查完整视频是否已缓存
   */
  async hasFullVideoCache(userId, songId) {
    console.log(`🎬 [视频缓存] 开始检查完整视频缓存:`, {
      userId,
      songId,
      timestamp: new Date().toISOString()
    });

    try {
      await this.ensureDBReady();
      console.log(`🎬 [视频缓存] 数据库连接正常`);
    } catch (error) {
      console.error('🎬 [视频缓存] ❌ 数据库初始化失败，无法检查缓存:', {
        songId,
        error: error.message,
        stack: error.stack
      });
      return false;
    }
    
    try {
      const cacheKey = this.generateCacheKey(userId, songId, 'video');
      console.log('🎬 [视频缓存] 生成的缓存键:', {
        userId,
        songId,
        cacheKey,
        storeName: VIDEO_CACHE_STORE
      });
      
      // 直接查询特定缓存，避免获取所有缓存数据
      console.log(`🎬 [视频缓存] 开始数据库查询...`);
      const result = await this._dbGet(VIDEO_CACHE_STORE, cacheKey);
      console.log('🎬 [视频缓存] 数据库查询结果:', {
        hasResult: !!result,
        resultType: result ? typeof result : 'undefined',
        resultKeys: result ? Object.keys(result) : [],
        songId
      });
      
      if (result) {
        // 🔥 永久缓存：不再检查过期时间，缓存永久有效
        console.log('🎬 [视频缓存] 找到完整视频缓存:', songId, '缓存时间:', new Date(result.timestamp).toLocaleString());
        return result;
      } else {
        // 检查旧的music-files存储中的视频缓存
        console.log('🎬 [视频缓存] 检查旧存储中的视频缓存...');
        const oldCacheKey = this.generateOldCacheKey(userId, songId);
        const oldCached = await this._dbGet('music-files', oldCacheKey);
        
        if (oldCached && oldCached.type === 'video/mp4') {
          console.log('🎬 [视频缓存] 找到旧存储中的视频缓存:', songId);
          // 迁移到新的存储结构
          await this.migrateOldVideoCache(userId, songId, oldCached);
          return await this._dbGet(VIDEO_CACHE_STORE, cacheKey);
        }
        
        console.log('🎬 [视频缓存] 未找到完整视频缓存:', songId);
      }
      
      return false;
    } catch (error) {
      console.error('❌ [视频缓存] 检查完整视频缓存失败:', error);
      return false;
    }
  }

  /**
   * 检查部分视频缓存
   */
  async hasPartialVideoCache(userId, songId) {
    console.log(`🎬 [视频缓存] 开始检查部分视频缓存:`, {
      userId,
      songId,
      timestamp: new Date().toISOString()
    });

    try {
      await this.ensureDBReady();
      console.log(`🎬 [视频缓存] 数据库连接正常`);
    } catch (error) {
      console.error('🎬 [视频缓存] ❌ 数据库初始化失败，无法检查部分缓存:', {
        songId,
        error: error.message,
        stack: error.stack
      });
      return false;
    }
    
    try {
      const cacheKey = this.generateCacheKey(userId, songId, 'video');
      console.log('🎬 [视频缓存] 生成的缓存键:', {
        userId,
        songId,
        cacheKey,
        storeName: PARTIAL_CACHE_STORE
      });
      
      console.log(`🎬 [视频缓存] 开始部分缓存数据库查询...`);
      const result = await this._dbGet(PARTIAL_CACHE_STORE, cacheKey);
      console.log('🎬 [视频缓存] 部分缓存数据库查询结果:', {
        hasResult: !!result,
        resultType: result ? typeof result : 'undefined',
        resultKeys: result ? Object.keys(result) : [],
        songId
      });
      
      if (result) {
        // 🔥 永久缓存：不再检查过期时间，部分缓存也永久有效
        console.log('🎬 [视频缓存] 找到部分视频缓存:', songId, '已缓存:', result.cachedDuration, '秒', '缓存时间:', new Date(result.timestamp).toLocaleString());
        return result;
      } else {
        console.log('🎬 [视频缓存] 未找到部分视频缓存:', songId);
      }
      
      return false;
    } catch (error) {
      console.error('❌ [视频缓存] 检查部分视频缓存失败:', error);
      return false;
    }
  }

  /**
   * 缓存完整视频
   */
  async cacheFullVideo(userId, songId, videoBlob, metadata = {}) {
    if (!videoBlob) return false;
    
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法缓存完整视频:', error);
      return false;
    }
    
    try {
      const cacheKey = this.generateCacheKey(userId, songId, 'video');
      
      const cacheData = {
        cacheKey,
        userId,
        songId,
        videoBlob,
        metadata: {
          ...metadata,
          size: videoBlob.size,
          type: videoBlob.type || 'video/mp4'
        },
        timestamp: Date.now()
      };
      
      await this._dbPut(VIDEO_CACHE_STORE, cacheData);
      console.log('✅ [视频缓存] 完整视频缓存成功:', songId, '大小:', Math.round(videoBlob.size / 1024 / 1024), 'MB');
      
      // 删除对应的部分缓存
      await this.deletePartialVideoCache(userId, songId);
      
      // 🔥 新增：触发视频缓存成功事件，通知音乐列表更新UI
      this.triggerVideoCacheEvent('video-cached', {
        songId,
        userId,
        cacheKey,
        size: videoBlob.size,
        songInfo: metadata.songInfo || {}
      });
      
      return true;
    } catch (error) {
      console.error('❌ [视频缓存] 完整视频缓存失败:', error);
      return false;
    }
  }

  /**
   * 🔥 触发视频缓存事件
   */
  triggerVideoCacheEvent(eventType, data) {
    const event = new CustomEvent(`smart-cache-${eventType}`, {
      detail: data
    });
    console.log('🎬 [视频缓存] 触发缓存事件:', eventType, data);
    window.dispatchEvent(event);
  }

  /**
   * 🧠 智能码率检测 - 分析视频真实数据需求
   */
  async detectVideoBitrate(videoBlob) {
    try {
      // 创建临时video元素检测
      const video = document.createElement('video');
      const url = URL.createObjectURL(videoBlob);
      
      return new Promise((resolve) => {
        video.onloadedmetadata = () => {
          const duration = video.duration;
          const size = videoBlob.size;
          const bitrate = (size * 8) / duration; // bps
          const bytesPerSecond = size / duration; // B/s
          
          console.log('📊 [码率检测] 视频信息:', {
            duration: Math.round(duration),
            size: Math.round(size / 1024 / 1024) + 'MB',
            bitrate: Math.round(bitrate / 1000) + 'kbps',
            bytesPerSecond: Math.round(bytesPerSecond / 1024) + 'KB/s'
          });
          
          URL.revokeObjectURL(url);
          resolve({
            duration,
            size,
            bitrate,
            bytesPerSecond
          });
        };
        
        video.onerror = () => {
          console.warn('⚠️ [码率检测] 无法检测，使用默认值');
          URL.revokeObjectURL(url);
          resolve({
            duration: 0,
            size: videoBlob.size,
            bitrate: 1000000, // 1Mbps 默认
            bytesPerSecond: 125000 // 125KB/s 默认
          });
        };
        
        video.src = url;
      });
    } catch (error) {
      console.warn('⚠️ [码率检测] 检测失败，使用默认值:', error);
      return {
        duration: 0,
        size: videoBlob.size,
        bitrate: 1000000,
        bytesPerSecond: 125000
      };
    }
  }

  /**
   * 🧠 计算智能缓冲大小 - 基于实际码率
   */
  calculateSmartBufferSize(bytesPerSecond, targetSeconds = 5) {
    const bufferSize = bytesPerSecond * targetSeconds;
    const minBuffer = 512 * 1024; // 最小512KB
    const maxBuffer = 10 * 1024 * 1024; // 最大10MB
    
    const smartSize = Math.max(minBuffer, Math.min(maxBuffer, bufferSize));
    
    console.log('🧠 [智能缓冲] 计算缓冲大小:', {
      bytesPerSecond: Math.round(bytesPerSecond / 1024) + 'KB/s',
      targetSeconds,
      calculatedSize: Math.round(bufferSize / 1024) + 'KB',
      finalSize: Math.round(smartSize / 1024) + 'KB'
    });
    
    return smartSize;
  }

  /**
   * 🚀 智能下载并播放视频（边下载边播放）
   * 基于实际视频码率动态计算缓冲大小，实现真正的流式播放
   */
  async downloadAndPlayVideo(userId, songId, videoUrl, onProgress = null) {
    try {
      console.log('🎬 [智能播放] 开始下载视频数据:', songId, 'URL:', videoUrl);
      
      // 检查是否已有完整缓存
      const existingCache = await this.getFullVideoCache(userId, songId);
      if (existingCache && existingCache.blobUrl) {
        console.log('🎬 [智能播放] 发现完整缓存，直接使用:', songId);
        return {
          blobUrl: existingCache.blobUrl,
          isFromCache: true,
          size: existingCache.size
        };
      }
      
      // 开始下载视频数据
      const response = await fetch(videoUrl);
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status} ${response.statusText}`);
      }
      
      const contentLength = response.headers.get('content-length');
      const totalSize = contentLength ? parseInt(contentLength) : 0;
      
      console.log('🎬 [智能播放] 开始下载，总大小:', totalSize ? Math.round(totalSize / 1024 / 1024) + 'MB' : '未知');
      
      // 创建读取器
      const reader = response.body.getReader();
      const chunks = [];
      let downloadedSize = 0;
      let segmentIndex = 0;
      let videoInfo = null;
      let smartBufferSize = 1024 * 1024; // 默认1MB
      let canStartPlayback = false;
      let playbackStarted = false;
      
      // 分片缓存管理器
      const segmentCache = new Map();
      let playbackBlobUrl = null;
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        downloadedSize += value.length;
        
        // 更新下载进度
        if (onProgress && totalSize > 0) {
          const progress = Math.round((downloadedSize / totalSize) * 100);
          onProgress(progress, downloadedSize, totalSize);
        }
        
        // 第一次检测码率（当有足够数据时）
        if (!videoInfo && downloadedSize >= 512 * 1024) { // 512KB后开始检测
          const tempBlob = new Blob(chunks, { type: 'video/mp4' });
          videoInfo = await this.detectVideoBitrate(tempBlob);
          smartBufferSize = this.calculateSmartBufferSize(videoInfo.bytesPerSecond, 5);
          console.log('🧠 [智能播放] 码率检测完成，智能缓冲大小:', Math.round(smartBufferSize / 1024) + 'KB');
        }
        
        // 检查是否可以开始播放
        if (!playbackStarted && downloadedSize >= smartBufferSize) {
          const playbackBlob = new Blob(chunks, { type: 'video/mp4' });
          playbackBlobUrl = URL.createObjectURL(playbackBlob);
          canStartPlayback = true;
          playbackStarted = true;
          
          console.log('🚀 [智能播放] 缓冲完成，可以开始播放! 缓冲大小:', Math.round(downloadedSize / 1024) + 'KB');
          
          // 立即返回可播放的Blob URL
          return {
            blobUrl: playbackBlobUrl,
            isFromCache: false,
            size: downloadedSize,
            canStartPlayback: true,
            continueDownload: this.continueVideoDownload(userId, songId, reader, chunks, segmentIndex, segmentCache, onProgress, totalSize, downloadedSize)
          };
        }
        
        // 检查是否需要创建分片
        if (chunks.length > 0 && downloadedSize >= 1024 * 1024) { // 1MB分片
          const segmentBlob = new Blob(chunks, { type: 'video/mp4' });
          
          // 缓存分片
          await this.cacheVideoSegment(userId, songId, segmentIndex, segmentBlob);
          segmentCache.set(segmentIndex, segmentBlob);
          
          console.log('🎬 [智能播放] 分片缓存完成:', segmentIndex, '大小:', Math.round(segmentBlob.size / 1024), 'KB');
          
          // 重置chunks，准备下一个分片
          chunks.length = 0;
          segmentIndex++;
        }
      }
      
      // 处理最后一个分片
      if (chunks.length > 0) {
        const segmentBlob = new Blob(chunks, { type: 'video/mp4' });
        await this.cacheVideoSegment(userId, songId, segmentIndex, segmentBlob);
        segmentCache.set(segmentIndex, segmentBlob);
        console.log('🎬 [智能播放] 最后分片缓存完成:', segmentIndex, '大小:', Math.round(segmentBlob.size / 1024), 'KB');
      }
      
      // 合并所有分片为完整视频
      const allSegments = Array.from(segmentCache.values());
      const fullVideoBlob = new Blob(allSegments, { type: 'video/mp4' });
      
      // 创建Blob URL用于播放
      const blobUrl = URL.createObjectURL(fullVideoBlob);
      
      // 缓存完整视频
      await this.cacheFullVideo(userId, songId, fullVideoBlob, {
        originalUrl: videoUrl,
        downloadTime: Date.now(),
        segmentCount: segmentIndex + 1,
        totalSize: fullVideoBlob.size
      });
      
      console.log('✅ [智能播放] 视频下载完成，分片数:', segmentIndex + 1, '总大小:', Math.round(fullVideoBlob.size / 1024 / 1024), 'MB');
      
      return {
        blobUrl,
        isFromCache: false,
        size: fullVideoBlob.size,
        segmentCount: segmentIndex + 1
      };
      
    } catch (error) {
      console.error('❌ [智能播放] 下载失败:', error);
      throw error;
    }
  }

  /**
   * 🔄 继续下载剩余视频数据
   */
  async continueVideoDownload(userId, songId, reader, chunks, segmentIndex, segmentCache, onProgress, totalSize, downloadedSize) {
    try {
      console.log('🔄 [继续下载] 开始下载剩余数据...');
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        downloadedSize += value.length;
        
        // 更新下载进度
        if (onProgress && totalSize > 0) {
          const progress = Math.round((downloadedSize / totalSize) * 100);
          onProgress(progress, downloadedSize, totalSize);
        }
        
        // 检查是否需要创建分片
        if (chunks.length > 0 && downloadedSize >= 1024 * 1024) {
          const segmentBlob = new Blob(chunks, { type: 'video/mp4' });
          
          // 缓存分片
          await this.cacheVideoSegment(userId, songId, segmentIndex, segmentBlob);
          segmentCache.set(segmentIndex, segmentBlob);
          
          console.log('🔄 [继续下载] 分片缓存完成:', segmentIndex, '大小:', Math.round(segmentBlob.size / 1024), 'KB');
          
          // 重置chunks，准备下一个分片
          chunks.length = 0;
          segmentIndex++;
        }
      }
      
      // 处理最后一个分片
      if (chunks.length > 0) {
        const segmentBlob = new Blob(chunks, { type: 'video/mp4' });
        await this.cacheVideoSegment(userId, songId, segmentIndex, segmentBlob);
        segmentCache.set(segmentIndex, segmentBlob);
        console.log('🔄 [继续下载] 最后分片缓存完成:', segmentIndex, '大小:', Math.round(segmentBlob.size / 1024), 'KB');
      }
      
      console.log('✅ [继续下载] 剩余数据下载完成');
      return true;
      
    } catch (error) {
      console.error('❌ [继续下载] 下载失败:', error);
      return false;
    }
  }

  /**
   * 🚀 新增：缓存视频分片（1MB分片）
   */
  async cacheVideoSegment(userId, songId, segmentIndex, segmentBlob) {
    try {
      await this.ensureDBReady();
      
      const segmentKey = `${userId}_${songId}_${segmentIndex}`;
      
      const segmentData = {
        segmentKey,
        userId,
        songId,
        segmentIndex,
        segmentBlob,
        size: segmentBlob.size,
        timestamp: Date.now(),
        metadata: {
          type: segmentBlob.type || 'video/mp4',
          isSegment: true
        }
      };
      
      await this._dbPut(PARTIAL_CACHE_STORE, segmentData);
      console.log('✅ [分片缓存] 分片缓存成功:', segmentKey, '大小:', Math.round(segmentBlob.size / 1024), 'KB');
      
      return true;
    } catch (error) {
      console.error('❌ [分片缓存] 分片缓存失败:', error);
      return false;
    }
  }

  /**
   * 🚀 新增：获取视频分片
   */
  async getVideoSegment(userId, songId, segmentIndex) {
    try {
      await this.ensureDBReady();
      
      const segmentKey = `${userId}_${songId}_${segmentIndex}`;
      const segmentData = await this._dbGet(PARTIAL_CACHE_STORE, segmentKey);
      
      return segmentData;
    } catch (error) {
      console.error('❌ [分片缓存] 获取分片失败:', error);
      return null;
    }
  }

  /**
   * 🚀 新增：检查视频是否已完全下载
   */
  async isVideoFullyDownloaded(userId, songId) {
    try {
      // 检查是否有完整缓存
      const fullCache = await this.getFullVideoCache(userId, songId);
      if (fullCache) {
        return { isComplete: true, cacheType: 'full' };
      }
      
      // 检查分片缓存情况
      const segments = await this.getVideoSegments(userId, songId);
      if (segments && segments.length > 0) {
        return { isComplete: false, cacheType: 'segments', segmentCount: segments.length };
      }
      
      return { isComplete: false, cacheType: 'none' };
    } catch (error) {
      console.error('❌ [分片缓存] 检查下载状态失败:', error);
      return { isComplete: false, cacheType: 'error' };
    }
  }

  /**
   * 🚀 新增：获取所有视频分片
   */
  async getVideoSegments(userId, songId) {
    try {
      await this.ensureDBReady();
      
      const transaction = this.db.transaction([PARTIAL_CACHE_STORE], 'readonly');
      const store = transaction.objectStore(PARTIAL_CACHE_STORE);
      const index = store.index('userId_songId');
      const range = IDBKeyRange.only([userId, songId]);
      
      const segments = await new Promise((resolve, reject) => {
        const request = index.getAll(range);
        request.onsuccess = () => {
          const results = request.result.filter(item => item.metadata?.isSegment);
          resolve(results.sort((a, b) => a.segmentIndex - b.segmentIndex));
        };
        request.onerror = () => reject(request.error);
      });
      
      return segments;
    } catch (error) {
      console.error('❌ [分片缓存] 获取分片列表失败:', error);
      return [];
    }
  }

  /**
   * 🚀 新增：合并分片为完整视频
   */
  async mergeSegmentsToFullVideo(userId, songId) {
    try {
      console.log('🔄 [分片合并] 开始合并分片为完整视频:', songId);
      
      // 获取所有分片
      const segments = await this.getVideoSegments(userId, songId);
      if (!segments || segments.length === 0) {
        console.log('⚠️ [分片合并] 没有找到分片数据');
        return false;
      }
      
      console.log('🔄 [分片合并] 找到分片数量:', segments.length);
      
      // 按索引排序
      segments.sort((a, b) => a.segmentIndex - b.segmentIndex);
      
      // 合并所有分片
      const segmentBlobs = segments.map(segment => segment.segmentBlob);
      const fullVideoBlob = new Blob(segmentBlobs, { type: 'video/mp4' });
      
      // 缓存完整视频
      await this.cacheFullVideo(userId, songId, fullVideoBlob, {
        mergedFromSegments: true,
        segmentCount: segments.length,
        mergeTime: Date.now()
      });
      
      // 清理分片缓存
      await this.clearVideoSegments(userId, songId);
      
      console.log('✅ [分片合并] 分片合并完成，总大小:', Math.round(fullVideoBlob.size / 1024 / 1024), 'MB');
      
      return true;
    } catch (error) {
      console.error('❌ [分片合并] 合并失败:', error);
      return false;
    }
  }

  /**
   * 🚀 新增：清理视频分片
   */
  async clearVideoSegments(userId, songId) {
    try {
      await this.ensureDBReady();
      
      const transaction = this.db.transaction([PARTIAL_CACHE_STORE], 'readwrite');
      const store = transaction.objectStore(PARTIAL_CACHE_STORE);
      const index = store.index('userId_songId');
      const range = IDBKeyRange.only([userId, songId]);
      
      await new Promise((resolve, reject) => {
        const request = index.openCursor(range);
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const data = cursor.value;
            if (data.metadata?.isSegment) {
              cursor.delete();
            }
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });
      
      console.log('✅ [分片清理] 分片缓存清理完成:', songId);
      return true;
    } catch (error) {
      console.error('❌ [分片清理] 清理失败:', error);
      return false;
    }
  }

  /**
   * 缓存部分视频（用户已播放的部分）
   */
  async cachePartialVideo(userId, songId, videoBlob, playedDuration, totalDuration, metadata = {}) {
    if (!videoBlob) return false;
    
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法缓存部分视频:', error);
      return false;
    }
    
    try {
      const cacheKey = this.generateCacheKey(userId, songId, 'video');
      
      const cacheData = {
        cacheKey,
        userId,
        songId,
        videoBlob,
        playedDuration,
        totalDuration,
        cachedDuration: playedDuration,
        metadata: {
          ...metadata,
          size: videoBlob.size,
          type: videoBlob.type || 'video/mp4'
        },
        timestamp: Date.now()
      };
      
      await this._dbPut(PARTIAL_CACHE_STORE, cacheData);
      console.log('✅ [视频缓存] 部分视频缓存成功:', songId, '已缓存:', playedDuration, '秒', '大小:', Math.round(videoBlob.size / 1024 / 1024), 'MB');
      
      return true;
    } catch (error) {
      console.error('❌ [视频缓存] 部分视频缓存失败:', error);
      return false;
    }
  }

  /**
   * 实时缓存已播放的视频部分
   * 通过MediaRecorder API录制已播放的部分
   */
  async startRealTimeCache(userId, songId, videoElement, metadata = {}) {
    if (!videoElement || !videoElement.src) {
      console.warn('⚠️ [视频缓存] 视频元素无效，无法开始实时缓存');
      return null;
    }

    try {
      // 创建MediaRecorder来录制视频流
      const stream = videoElement.captureStream ? videoElement.captureStream() : null;
      if (!stream) {
        console.warn('⚠️ [视频缓存] 无法获取视频流，浏览器可能不支持captureStream');
        return null;
      }

      // 检查流是否有音频轨道
      const audioTracks = stream.getAudioTracks();
      const videoTracks = stream.getVideoTracks();
      
      console.log('🎬 [视频缓存] 流轨道信息:', {
        audioTracks: audioTracks.length,
        videoTracks: videoTracks.length,
        audioTrackInfo: audioTracks.map(track => ({
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState
        }))
      });

      // 如果没有音频轨道，添加一个静音音频轨道
      if (audioTracks.length === 0) {
        console.log('🎬 [视频缓存] 检测到无音频轨道，添加静音音频轨道');
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          // 设置静音
          gainNode.gain.value = 0;
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          oscillator.start();
          
          // 创建音频轨道
          const audioDestination = audioContext.createMediaStreamDestination();
          gainNode.connect(audioDestination);
          
          // 将音频轨道添加到流中
          audioDestination.stream.getAudioTracks().forEach(track => {
            stream.addTrack(track);
          });
          
          console.log('🎬 [视频缓存] 静音音频轨道添加成功');
        } catch (audioError) {
          console.warn('⚠️ [视频缓存] 添加静音音频轨道失败:', audioError);
          // 继续使用没有音频轨道的流
        }
      }

      // 检查MediaRecorder支持的MIME类型
      let mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            console.warn('⚠️ [视频缓存] 浏览器不支持MediaRecorder，使用备用方案');
            return null;
          }
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType
      });

      const chunks = [];
      let startTime = Date.now();
      let isRecording = false;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (chunks.length > 0) {
          const recordedBlob = new Blob(chunks, { type: 'video/webm' });
          const playedDuration = (Date.now() - startTime) / 1000;
          
          // 缓存录制的部分
          await this.cachePartialVideo(
            userId, 
            songId, 
            recordedBlob, 
            playedDuration, 
            videoElement.duration || 0,
            {
              ...metadata,
              cacheMethod: 'realtime',
              originalUrl: videoElement.src
            }
          );
          
          console.log(`🎬 [视频缓存] 实时缓存完成: ${songId}, 录制时长: ${playedDuration.toFixed(1)}s`);
        }
      };

      // 开始录制
      mediaRecorder.start(1000); // 每秒收集一次数据
      isRecording = true;

      console.log(`🎬 [视频缓存] 开始实时缓存: ${songId}`);

      return {
        mediaRecorder,
        stop: () => {
          if (isRecording && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
            isRecording = false;
            console.log(`⏹️ [视频缓存] 停止实时缓存: ${songId}`);
          }
        },
        isRecording: () => isRecording
      };
    } catch (error) {
      console.error('❌ [视频缓存] 启动实时缓存失败:', error);
      return null;
    }
  }

  /**
   * 通过Range请求缓存已播放的部分
   * 这是一个更精确的方法，直接获取已播放的字节范围
   */
  async cachePlayedRange(userId, songId, videoElement, playedDuration, metadata = {}) {
    if (!videoElement || !videoElement.src || playedDuration <= 0) {
      return false;
    }

    try {
      // 计算已播放的字节范围
      const totalDuration = videoElement.duration || 0;
      const contentLength = await this.getContentLength(videoElement.src);
      
      if (!contentLength || totalDuration <= 0) {
        console.warn('⚠️ [视频缓存] 无法获取视频长度信息');
        return false;
      }

      // 计算已播放部分对应的字节范围
      const playedRatio = playedDuration / totalDuration;
      const endByte = Math.floor(contentLength * playedRatio);
      
      // 使用Range请求获取已播放的部分
      const response = await fetch(videoElement.src, {
        headers: {
          'Range': `bytes=0-${endByte}`
        }
      });

      if (!response.ok) {
        console.warn('⚠️ [视频缓存] Range请求失败，服务器可能不支持Range请求');
        return false;
      }

      const videoBlob = await response.blob();
      
      // 缓存获取到的部分
      await this.cachePartialVideo(
        userId,
        songId,
        videoBlob,
        playedDuration,
        totalDuration,
        {
          ...metadata,
          cacheMethod: 'range',
          originalUrl: videoElement.src,
          rangeEnd: endByte,
          contentLength
        }
      );

      console.log(`✅ [视频缓存] Range缓存成功: ${songId}, 已播放: ${playedDuration}s, 缓存字节: 0-${endByte}`);
      return true;
    } catch (error) {
      console.error('❌ [视频缓存] Range缓存失败:', error);
      return false;
    }
  }

  /**
   * 获取视频文件的Content-Length
   */
  async getContentLength(url) {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const contentLength = response.headers.get('Content-Length');
      return contentLength ? parseInt(contentLength) : null;
    } catch (error) {
      console.warn('⚠️ [视频缓存] 获取Content-Length失败:', error);
      return null;
    }
  }

  /**
   * 验证视频缓存是否有效
   */
  async validateVideoCache(blobUrl) {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.crossOrigin = 'anonymous';
      
      const timeout = setTimeout(() => {
        console.warn('🎬 [视频缓存] 视频验证超时');
        video.remove();
        resolve(false);
      }, 8000); // 增加超时时间到8秒
      
      let hasLoadedMetadata = false;
      let hasCanPlay = false;
      
      video.onloadedmetadata = () => {
        hasLoadedMetadata = true;
        console.log('🎬 [视频缓存] 视频元数据加载成功');
        
        // 检查视频时长是否有效
        if (video.duration && video.duration > 0 && video.duration !== Infinity) {
          console.log('🎬 [视频缓存] 视频时长有效:', video.duration);
        } else {
          console.warn('🎬 [视频缓存] 视频时长无效:', video.duration);
          clearTimeout(timeout);
          video.remove();
          resolve(false);
          return;
        }
        
        // 检查视频尺寸是否有效
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          console.log('🎬 [视频缓存] 视频尺寸有效:', video.videoWidth, 'x', video.videoHeight);
        } else {
          console.warn('🎬 [视频缓存] 视频尺寸无效:', video.videoWidth, 'x', video.videoHeight);
          clearTimeout(timeout);
          video.remove();
          resolve(false);
          return;
        }
      };
      
      video.oncanplay = () => {
        hasCanPlay = true;
        console.log('🎬 [视频缓存] 视频可以播放');
        
        // 双重验证：既有元数据又能播放
        if (hasLoadedMetadata && hasCanPlay) {
          clearTimeout(timeout);
          console.log('🎬 [视频缓存] 视频缓存验证成功');
          video.remove();
          resolve(true);
        }
      };
      
      video.onerror = (error) => {
        clearTimeout(timeout);
        console.warn('🎬 [视频缓存] 视频缓存验证失败:', error);
        video.remove();
        resolve(false);
      };
      
      video.onstalled = () => {
        console.warn('🎬 [视频缓存] 视频加载停滞');
      };
      
      video.onabort = () => {
        console.warn('🎬 [视频缓存] 视频加载被中止');
      };
      
      video.src = blobUrl;
    });
  }

  /**
   * 获取完整视频缓存
   */
  async getFullVideoCache(userId, songId) {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法获取完整视频缓存:', error);
      return null;
    }
    
    try {
      const cacheKey = this.generateCacheKey(userId, songId, 'video');
      const result = await this._dbGet(VIDEO_CACHE_STORE, cacheKey);
      
      if (result && result.videoBlob) {
        // 🔍 额外校验：防止错误的视频内容被写入了当前歌曲的缓存键
        try {
          const recordUserId = String(result.userId ?? '');
          const recordSongId = String(result.songId ?? '');
          const expectedUserId = String(userId ?? '');
          const expectedSongId = String(songId ?? '');

          if (recordUserId && recordSongId &&
              (recordUserId !== expectedUserId || recordSongId !== expectedSongId)) {
            console.warn('⚠️ [视频缓存] 检测到缓存元数据与请求的用户/歌曲ID不一致，视为损坏缓存并删除:', {
              cacheKey,
              expectedUserId,
              expectedSongId,
              recordUserId,
              recordSongId
            });
            await this._dbDelete(VIDEO_CACHE_STORE, cacheKey);
            return null;
          }
        } catch (metaError) {
          console.warn('⚠️ [视频缓存] 校验缓存元数据时出错，继续按照正常流程验证视频内容:', metaError);
        }

        const blobUrl = URL.createObjectURL(result.videoBlob);
        
        // 🎬 新增：验证视频缓存是否有效
        console.log('🎬 [视频缓存] 验证视频缓存有效性...');
        const isValid = await this.validateVideoCache(blobUrl);
        
        if (isValid) {
          console.log('🎬 [视频缓存] 返回完整视频缓存:', songId);
          return {
            blobUrl,
            metadata: result.metadata,
            timestamp: result.timestamp
          };
        } else {
          console.warn('🎬 [视频缓存] 视频缓存已损坏，立即删除缓存:', songId);
          // 立即清理损坏的缓存
          try {
            await this._dbDelete(VIDEO_CACHE_STORE, cacheKey);
            console.log('🗑️ [视频缓存] 已删除损坏的完整视频缓存:', cacheKey);
          } catch (error) {
            console.error('❌ [视频缓存] 删除损坏缓存失败:', error);
          }
          URL.revokeObjectURL(blobUrl);
          return null;
        }
      }
      
      return null;
    } catch (error) {
      console.error('❌ [视频缓存] 获取完整视频缓存失败:', error);
      return null;
    }
  }

  /**
   * 获取部分视频缓存
   */
  async getPartialVideoCache(userId, songId) {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法获取部分视频缓存:', error);
      return null;
    }
    
    try {
      const cacheKey = this.generateCacheKey(userId, songId, 'video');
      const result = await this._dbGet(PARTIAL_CACHE_STORE, cacheKey);
      
      if (result && result.videoBlob) {
        // 🔍 额外校验：防止错误的视频内容被写入了当前歌曲的部分缓存键
        try {
          const recordUserId = String(result.userId ?? '');
          const recordSongId = String(result.songId ?? '');
          const expectedUserId = String(userId ?? '');
          const expectedSongId = String(songId ?? '');

          if (recordUserId && recordSongId &&
              (recordUserId !== expectedUserId || recordSongId !== expectedSongId)) {
            console.warn('⚠️ [视频缓存] 检测到部分缓存元数据与请求的用户/歌曲ID不一致，视为损坏缓存并删除:', {
              cacheKey,
              expectedUserId,
              expectedSongId,
              recordUserId,
              recordSongId
            });
            await this._dbDelete(PARTIAL_CACHE_STORE, cacheKey);
            return null;
          }
        } catch (metaError) {
          console.warn('⚠️ [视频缓存] 校验部分缓存元数据时出错，继续按照正常流程验证视频内容:', metaError);
        }

        const blobUrl = URL.createObjectURL(result.videoBlob);
        
        // 🎬 新增：验证部分视频缓存是否有效
        console.log('🎬 [视频缓存] 验证部分视频缓存有效性...');
        const isValid = await this.validateVideoCache(blobUrl);
        
        if (isValid) {
          console.log('🎬 [视频缓存] 返回部分视频缓存:', songId, '已缓存:', result.cachedDuration, '秒');
          return {
            blobUrl,
            cachedDuration: result.cachedDuration,
            totalDuration: result.totalDuration,
            metadata: result.metadata,
            timestamp: result.timestamp
          };
        } else {
          console.warn('🎬 [视频缓存] 部分视频缓存已损坏，立即删除缓存:', songId);
          // 立即清理损坏的缓存
          try {
            await this._dbDelete(PARTIAL_CACHE_STORE, cacheKey);
            console.log('🗑️ [视频缓存] 已删除损坏的部分视频缓存:', cacheKey);
          } catch (error) {
            console.error('❌ [视频缓存] 删除损坏部分缓存失败:', error);
          }
          URL.revokeObjectURL(blobUrl);
          return null;
        }
      }
      
      return null;
    } catch (error) {
      console.error('❌ [视频缓存] 获取部分视频缓存失败:', error);
      return null;
    }
  }

  /**
   * 获取缓存的视频数据（统一接口）
   * 优先返回完整缓存，如果没有则返回部分缓存
   */
  async getCachedVideo(userId, songId) {
    try {
      console.log('🎬 [视频缓存] 开始获取缓存视频:', { userId, songId });
      
      // 先尝试获取完整缓存
      const fullCache = await this.getFullVideoCache(userId, songId);
      if (fullCache) {
        console.log('🎬 [视频缓存] 返回完整视频缓存:', songId);
        return fullCache;
      }
      
      // 再尝试获取部分缓存
      const partialCache = await this.getPartialVideoCache(userId, songId);
      if (partialCache) {
        console.log('🎬 [视频缓存] 返回部分视频缓存:', songId);
        return partialCache;
      }
      
      console.log('🎬 [视频缓存] 未找到任何视频缓存:', songId);
      return null;
    } catch (error) {
      console.error('❌ [视频缓存] 获取缓存视频失败:', error);
      return null;
    }
  }

  /**
   * 删除完整视频缓存
   */
  async deleteFullVideoCache(userId, songId) {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法删除完整视频缓存:', error);
      return false;
    }
    
    try {
      const cacheKey = this.generateCacheKey(userId, songId, 'video');
      await this._dbDelete(VIDEO_CACHE_STORE, cacheKey);
      console.log('🗑️ [视频缓存] 删除完整视频缓存:', songId);
      return true;
    } catch (error) {
      console.error('❌ [视频缓存] 删除完整视频缓存失败:', error);
      return false;
    }
  }

  /**
   * 删除部分视频缓存
   */
  async deletePartialVideoCache(userId, songId) {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法删除部分视频缓存:', error);
      return false;
    }
    
    try {
      const cacheKey = this.generateCacheKey(userId, songId, 'video');
      await this._dbDelete(PARTIAL_CACHE_STORE, cacheKey);
      console.log('🗑️ [视频缓存] 删除部分视频缓存:', songId);
      return true;
    } catch (error) {
      console.error('❌ [视频缓存] 删除部分视频缓存失败:', error);
      return false;
    }
  }

  /**
   * 下载视频并缓存
   */
  async downloadAndCacheVideo(url, userId, songId, onProgress = null) {
    try {
      console.log('🎬 [视频缓存] 开始下载视频:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status} ${response.statusText}`);
      }
      
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      let loaded = 0;
      
      const reader = response.body.getReader();
      const chunks = [];
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        loaded += value.length;
        
        // 更新进度
        if (onProgress && total > 0) {
          const progress = Math.round((loaded / total) * 100);
          onProgress(progress, loaded, total);
        }
      }
      
      // 合并所有chunks为Blob
      const videoBlob = new Blob(chunks, { type: 'video/mp4' });
      console.log('✅ [视频缓存] 视频下载完成:', Math.round(videoBlob.size / 1024 / 1024), 'MB');
      
      // 缓存完整视频
      await this.cacheFullVideo(userId, songId, videoBlob, {
        originalUrl: url,
        downloadTime: Date.now()
      });
      
      return videoBlob;
    } catch (error) {
      console.error('❌ [视频缓存] 视频下载失败:', error);
      throw error;
    }
  }

  /**
   * 智能缓存策略：文件加载完毕立即缓存
   */
  async smartCacheVideo(userId, songId, videoElement, onProgress = null) {
    try {
      const video = videoElement;
      if (!video || !video.src) return false;
      
      // 🔥 关键修复：保存当时的视频src，用于后续验证视频元素是否仍在播放同一首歌
      const expectedSrc = video.src;
      
      // 监听视频加载完成事件
      const handleLoadedData = () => {
        // 🔥 关键修复：在事件触发时验证视频元素是否仍在播放同一首歌
        if (video.src !== expectedSrc) {
          console.warn('⚠️ [视频缓存] loadeddata触发时视频源已变化，跳过缓存:', {
            songId,
            expectedSrc: expectedSrc.substring(0, 80),
            currentSrc: video.src.substring(0, 80)
          });
          video.removeEventListener('loadeddata', handleLoadedData);
          return;
        }
        
        // 检查视频是否已加载完成
        if (video.readyState >= 4) { // HAVE_ENOUGH_DATA
          console.log('🎬 [视频缓存] 文件加载完毕，立即开始缓存:', songId);
          this.cacheFullVideoFromElement(userId, songId, video, expectedSrc);
          video.removeEventListener('loadeddata', handleLoadedData);
        }
      };
      
      // 如果已经加载完成，立即缓存
      if (video.readyState >= 4) {
        console.log('🎬 [视频缓存] 视频已加载完成，立即开始缓存:', songId);
        this.cacheFullVideoFromElement(userId, songId, video, expectedSrc);
      } else {
        // 否则监听加载完成事件
        video.addEventListener('loadeddata', handleLoadedData);
      }
      
      return true;
    } catch (error) {
      console.error('❌ [视频缓存] 智能缓存失败:', error);
      return false;
    }
  }

  /**
   * 缓存已播放的视频部分
   */
  async cachePlayedPortion(userId, songId, videoElement, playedDuration, totalDuration) {
    try {
      // 这里需要实现视频片段的提取和缓存
      // 由于浏览器限制，我们只能缓存整个视频文件
      // 但可以记录播放进度，用于后续的断点续播
      console.log('🎬 [视频缓存] 记录播放进度:', songId, '已播放:', playedDuration, '秒');
      
      // 可以在这里实现更复杂的视频片段缓存逻辑
      // 比如使用MediaRecorder API录制已播放的部分
      
    } catch (error) {
      console.error('❌ [视频缓存] 缓存播放部分失败:', error);
    }
  }

  /**
   * 从视频元素缓存完整视频（优化版：利用已加载的数据）
   * @param {string} expectedSrc - 缓存发起时记录的视频src，用于验证视频源未变化
   */
  async cacheFullVideoFromElement(userId, songId, videoElement, expectedSrc = null) {
    try {
      // 获取视频的原始URL
      const originalUrl = videoElement.src;
      
      // 🔥 关键修复：验证视频源是否仍然是预期的那首歌
      if (expectedSrc && originalUrl !== expectedSrc) {
        console.warn('⚠️ [视频缓存] 视频源已变化，放弃缓存以防错误写入:', {
          songId,
          expectedSrc: expectedSrc.substring(0, 80),
          currentSrc: originalUrl.substring(0, 80)
        });
        return false;
      }
      
      if (!originalUrl || originalUrl.startsWith('blob:')) {
        console.log('🎬 [视频缓存] 跳过blob URL的缓存');
        return false;
      }
      
      // 🔥 优化：尝试从浏览器缓存中获取已加载的视频数据
      try {
        console.log('🎬 [视频缓存] 尝试从浏览器缓存获取视频数据:', songId);
        
        // 使用fetch获取视频数据，浏览器可能会从缓存中返回
        const response = await fetch(originalUrl, {
          method: 'GET',
          cache: 'force-cache' // 强制使用缓存
        });
        
        if (response.ok) {
          const videoBlob = await response.blob();
          
          // 🔥 关键修复：fetch完成后再次验证视频源未变化
          if (expectedSrc && videoElement.src !== expectedSrc) {
            console.warn('⚠️ [视频缓存] fetch完成后视频源已变化，放弃缓存:', {
              songId,
              expectedSrc: expectedSrc.substring(0, 80),
              currentSrc: videoElement.src.substring(0, 80)
            });
            return false;
          }
          
          console.log('✅ [视频缓存] 从浏览器缓存获取视频数据成功:', songId, '大小:', Math.round(videoBlob.size / 1024 / 1024), 'MB');
          
          // 直接缓存获取到的blob数据
          await this.cacheFullVideo(userId, songId, videoBlob, {
            originalUrl: originalUrl,
            cacheMethod: 'browser-cache',
            downloadTime: Date.now()
          });
          
          return true;
        }
      } catch (cacheError) {
        console.log('🎬 [视频缓存] 浏览器缓存获取失败，回退到重新下载:', cacheError.message);
      }
      
      // 🔥 关键修复：下载前再次验证视频源未变化
      if (expectedSrc && videoElement.src !== expectedSrc) {
        console.warn('⚠️ [视频缓存] 下载前视频源已变化，放弃缓存:', {
          songId,
          expectedSrc: expectedSrc.substring(0, 80),
          currentSrc: videoElement.src.substring(0, 80)
        });
        return false;
      }
      
      // 回退方案：下载并缓存完整视频
      await this.downloadAndCacheVideo(originalUrl, userId, songId);
      return true;
    } catch (error) {
      console.error('❌ [视频缓存] 从元素缓存完整视频失败:', error);
      return false;
    }
  }

  /**
   * 🔥 永久缓存：不再清理过期缓存，缓存永久保存
   * 只有用户主动清理或缓存损坏时才会删除
   */
  async cleanExpiredCache() {
    try {
      console.log('🎬 [视频缓存] 永久缓存模式：跳过过期缓存清理，缓存永久保存');
      
      // 🚀 使用优化的批量验证方法
      const validationResults = await this.batchValidateVideoCache();
      console.log(`🎬 [视频缓存] 永久缓存模式：批量验证完成，清理了 ${validationResults.corrupted.length} 个损坏的缓存`);
      
    } catch (error) {
      console.error('❌ [视频缓存] 清理损坏缓存失败:', error);
    }
  }

  /**
   * 🚀 优化版：批量验证视频缓存（使用单一数据库连接）
   */
  async batchValidateVideoCache() {
    try {
      await this.ensureDBReady();
      
      console.log('🎬 [视频缓存] 开始批量验证缓存...');
      const startTime = Date.now();
      
      // 🔥 关键优化：使用单一事务处理所有验证
      const transaction = this.db.transaction([VIDEO_CACHE_STORE, PARTIAL_CACHE_STORE], 'readonly');
      const videoStore = transaction.objectStore(VIDEO_CACHE_STORE);
      const partialStore = transaction.objectStore(PARTIAL_CACHE_STORE);
      
      // 批量获取所有缓存数据
      const [fullCaches, partialCaches] = await Promise.all([
        this.getAllCachesFromStore(videoStore),
        this.getAllCachesFromStore(partialStore)
      ]);
      
      console.log(`🎬 [视频缓存] 获取缓存数据完成: 完整缓存${fullCaches.length}个, 部分缓存${partialCaches.length}个`);
      
      // 🔥 并发验证所有缓存（限制并发数避免内存溢出）
      const validationResults = await this.concurrentValidateCache([...fullCaches, ...partialCaches]);
      
      // 清理损坏的缓存
      if (validationResults.corrupted.length > 0) {
        await this.batchDeleteCorruptedCache(validationResults.corrupted);
      }
      
      const endTime = Date.now();
      console.log(`✅ [视频缓存] 批量验证完成，耗时: ${endTime - startTime}ms，清理损坏缓存: ${validationResults.corrupted.length}个`);
      
      return validationResults;
    } catch (error) {
      console.error('❌ [视频缓存] 批量验证失败:', error);
      return { valid: [], corrupted: [] };
    }
  }

  /**
   * 🚀 从对象存储中获取所有缓存数据
   */
  async getAllCachesFromStore(store) {
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * 🚀 并发验证缓存（限制并发数）
   */
  async concurrentValidateCache(caches, concurrency = 5) {
    const results = { valid: [], corrupted: [] };
    
    // 分批处理，避免同时创建太多video元素
    for (let i = 0; i < caches.length; i += concurrency) {
      const batch = caches.slice(i, i + concurrency);
      
      const batchPromises = batch.map(async (cache) => {
        if (!cache.videoBlob) return null;
        
        const blobUrl = URL.createObjectURL(cache.videoBlob);
        const isValid = await this.validateVideoCache(blobUrl);
        URL.revokeObjectURL(blobUrl);
        
        return { cache, isValid };
      });
      
      const batchResults = await Promise.all(batchPromises);
      
      batchResults.forEach(result => {
        if (result) {
          if (result.isValid) {
            results.valid.push(result.cache);
          } else {
            results.corrupted.push(result.cache);
          }
        }
      });
      
      console.log(`🎬 [视频缓存] 批次验证完成: ${i + batch.length}/${caches.length}`);
    }
    
    return results;
  }

  /**
   * 🚀 批量删除损坏的缓存
   */
  async batchDeleteCorruptedCache(corruptedCaches) {
    try {
      const transaction = this.db.transaction([VIDEO_CACHE_STORE, PARTIAL_CACHE_STORE], 'readwrite');
      const videoStore = transaction.objectStore(VIDEO_CACHE_STORE);
      const partialStore = transaction.objectStore(PARTIAL_CACHE_STORE);
      
      const deletePromises = corruptedCaches.map(cache => {
        const store = cache.cacheKey.includes('video') ? videoStore : partialStore;
        return new Promise((resolve, reject) => {
          const request = store.delete(cache.cacheKey);
          request.onsuccess = () => {
            console.log('🗑️ [视频缓存] 批量清理损坏缓存:', cache.cacheKey);
            resolve();
          };
          request.onerror = () => reject(request.error);
        });
      });
      
      await Promise.all(deletePromises);
      console.log(`✅ [视频缓存] 批量删除完成，共删除 ${corruptedCaches.length} 个损坏缓存`);
    } catch (error) {
      console.error('❌ [视频缓存] 批量删除损坏缓存失败:', error);
    }
  }

  /**
   * 🎬 原版：清理损坏的缓存（保留作为备用方案）
   */
  async cleanCorruptedCache() {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法清理损坏缓存:', error);
      return;
    }
    
    try {
      console.log('🎬 [视频缓存] 开始清理损坏的缓存...');
      let corruptedCount = 0;
      
      // 检查完整视频缓存
      const fullCacheKeys = await this._dbGetAllKeys(VIDEO_CACHE_STORE);
      for (const key of fullCacheKeys) {
        const cache = await this._dbGet(VIDEO_CACHE_STORE, key);
        if (cache && cache.videoBlob) {
          const blobUrl = URL.createObjectURL(cache.videoBlob);
          const isValid = await this.validateVideoCache(blobUrl);
          URL.revokeObjectURL(blobUrl);
          
          if (!isValid) {
            await this._dbDelete(VIDEO_CACHE_STORE, key);
            console.log('🗑️ [视频缓存] 清理损坏的完整视频缓存:', key);
            corruptedCount++;
          }
        }
      }
      
      // 检查部分视频缓存
      const partialCacheKeys = await this._dbGetAllKeys(PARTIAL_CACHE_STORE);
      for (const key of partialCacheKeys) {
        const cache = await this._dbGet(PARTIAL_CACHE_STORE, key);
        if (cache && cache.videoBlob) {
          const blobUrl = URL.createObjectURL(cache.videoBlob);
          const isValid = await this.validateVideoCache(blobUrl);
          URL.revokeObjectURL(blobUrl);
          
          if (!isValid) {
            await this._dbDelete(PARTIAL_CACHE_STORE, key);
            console.log('🗑️ [视频缓存] 清理损坏的部分视频缓存:', key);
            corruptedCount++;
          }
        }
      }
      
      console.log(`✅ [视频缓存] 损坏缓存清理完成，共清理 ${corruptedCount} 个损坏的缓存`);
      return corruptedCount;
    } catch (error) {
      console.error('❌ [视频缓存] 清理损坏缓存失败:', error);
      return 0;
    }
  }

  /**
   * 🎬 新增：重建损坏的缓存
   */
  async rebuildCorruptedCache(userId, songId) {
    try {
      console.log('🎬 [视频缓存] 开始重建损坏的缓存:', songId);
      
      // 清理损坏的缓存
      await this.deleteFullVideoCache(userId, songId);
      await this.deletePartialVideoCache(userId, songId);
      
      console.log('🎬 [视频缓存] 已清理损坏的缓存，将在下次播放时重新缓存');
      return true;
    } catch (error) {
      console.error('❌ [视频缓存] 重建损坏缓存失败:', error);
      return false;
    }
  }

  /**
   * 验证缓存键的用户隔离性
   * 确保不同用户的缓存不会混淆
   */
  async validateUserCacheIsolation() {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法验证缓存隔离:', error);
      return { isValid: false, message: '数据库初始化失败' };
    }
    
    try {
      const userCacheMap = new Map();
      
      // 检查完整视频缓存
      const fullVideoStore = this.db.transaction(VIDEO_CACHE_STORE, 'readonly').objectStore(VIDEO_CACHE_STORE);
      const fullVideoCursor = await fullVideoStore.openCursor();
      
      while (fullVideoCursor) {
        const record = fullVideoCursor.value;
        const { userId, songId, cacheKey } = record;
        
        // 验证缓存键格式
        const expectedKey = this.generateCacheKey(userId, songId, 'video');
        if (cacheKey !== expectedKey) {
          console.warn('⚠️ [视频缓存] 缓存键格式不匹配:', { 
            actual: cacheKey, 
            expected: expectedKey,
            userId, 
            songId 
          });
        }
        
        // 统计用户缓存
        if (!userCacheMap.has(userId)) {
          userCacheMap.set(userId, { full: 0, partial: 0 });
        }
        userCacheMap.get(userId).full++;
        
        await fullVideoCursor.continue();
      }
      
      // 检查部分视频缓存
      const partialStore = this.db.transaction(PARTIAL_CACHE_STORE, 'readonly').objectStore(PARTIAL_CACHE_STORE);
      const partialCursor = await partialStore.openCursor();
      
      while (partialCursor) {
        const record = partialCursor.value;
        const { userId, songId, cacheKey } = record;
        
        // 验证缓存键格式
        const expectedKey = this.generateCacheKey(userId, songId, 'video');
        if (cacheKey !== expectedKey) {
          console.warn('⚠️ [视频缓存] 缓存键格式不匹配:', { 
            actual: cacheKey, 
            expected: expectedKey,
            userId, 
            songId 
          });
        }
        
        // 统计用户缓存
        if (!userCacheMap.has(userId)) {
          userCacheMap.set(userId, { full: 0, partial: 0 });
        }
        userCacheMap.get(userId).partial++;
        
        await partialCursor.continue();
      }
      
      // 输出统计信息
      console.log('📊 [视频缓存] 用户缓存隔离验证结果:');
      for (const [userId, stats] of userCacheMap) {
        console.log(`  用户 ${userId}: 完整缓存 ${stats.full} 个, 部分缓存 ${stats.partial} 个`);
      }
      
      return { 
        isValid: true, 
        message: '缓存隔离验证通过',
        userStats: Object.fromEntries(userCacheMap)
      };
    } catch (error) {
      console.error('❌ [视频缓存] 缓存隔离验证失败:', error);
      return { isValid: false, message: '缓存隔离验证失败', error };
    }
  }

  /**
   * 获取缓存统计信息
   */
  async getCacheStats() {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法获取缓存统计:', error);
      return null;
    }
    
    try {
      const fullVideoCount = await this.db.count(VIDEO_CACHE_STORE);
      const partialCount = await this.db.count(PARTIAL_CACHE_STORE);
      
      // 计算总缓存大小
      let totalSize = 0;
      const fullVideoStore = this.db.transaction(VIDEO_CACHE_STORE, 'readonly').objectStore(VIDEO_CACHE_STORE);
      const fullVideoCursor = await fullVideoStore.openCursor();
      
      while (fullVideoCursor) {
        const record = fullVideoCursor.value;
        if (record.videoBlob) {
          totalSize += record.videoBlob.size;
        }
        await fullVideoCursor.continue();
      }
      
      const partialStore = this.db.transaction(PARTIAL_CACHE_STORE, 'readonly').objectStore(PARTIAL_CACHE_STORE);
      const partialCursor = await partialStore.openCursor();
      
      while (partialCursor) {
        const record = partialCursor.value;
        if (record.videoBlob) {
          totalSize += record.videoBlob.size;
        }
        await partialCursor.continue();
      }
      
      return {
        fullVideoCount,
        partialCount,
        totalSize: Math.round(totalSize / 1024 / 1024), // MB
        totalSizeBytes: totalSize
      };
    } catch (error) {
      console.error('❌ [视频缓存] 获取缓存统计失败:', error);
      return null;
    }
  }

  /**
   * 快速获取用户所有缓存歌曲ID（通过解析缓存键，更可靠）
   * @param {string} userId - 用户ID
   * @returns {Promise<Set>} 返回Set，包含所有有缓存的歌曲ID
   */
  async getCachedSongIds(userId) {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法获取缓存歌曲ID:', error);
      return new Set();
    }
    
    try {
      console.log('🎬 [视频缓存] 快速查询用户缓存歌曲ID:', userId, '类型:', typeof userId);
      
      // 🔥 优化方案：直接获取所有缓存键，然后解析键来提取 songId
      // 缓存键格式：userId_songId_video (例如: 22_2916_video)
      const allCacheKeys = await this._dbGetAllKeys(VIDEO_CACHE_STORE);
      console.log('🎬 [视频缓存] 所有视频缓存键数:', allCacheKeys.length);
      
      // 筛选出该用户的视频缓存键并提取 songId
      const cachedSongIds = new Set();
      const userIdStr = String(userId);
      
      allCacheKeys.forEach(key => {
        // 解析缓存键格式：userId_songId_video
        if (typeof key === 'string') {
          const parts = key.split('_');
          if (parts.length === 3 && parts[0] === userIdStr && parts[2] === 'video') {
            const songId = parts[1];
            cachedSongIds.add(songId);
            console.log(`🎬 [视频缓存] 从缓存键解析出歌曲ID: ${songId} (键: ${key})`);
          }
        }
      });
      
      console.log('🎬 [视频缓存] 快速查询完成，有效缓存歌曲数:', cachedSongIds.size);
      return cachedSongIds;
      
    } catch (error) {
      console.error('❌ [视频缓存] 快速查询缓存歌曲ID失败:', error);
      // 如果新方法失败，尝试回退到索引查询方式
      try {
        console.log('🎬 [视频缓存] 尝试使用索引查询作为回退方案...');
        const numericUserId = parseInt(userId);
        const userCaches = await this._dbGetByIndex(VIDEO_CACHE_STORE, 'userId', numericUserId);
        const cachedSongIds = new Set();
        userCaches.forEach(cache => {
          cachedSongIds.add(cache.songId);
        });
        console.log('🎬 [视频缓存] 索引查询完成，缓存歌曲数:', cachedSongIds.size);
        return cachedSongIds;
      } catch (fallbackError) {
        console.error('❌ [视频缓存] 回退方案也失败:', fallbackError);
        return new Set();
      }
    }
  }

  /**
   * 批量检查多个歌曲的缓存状态（优化版本）
   * @param {string} userId - 用户ID
   * @param {Array} songIds - 歌曲ID数组
   * @returns {Promise<Map>} 返回Map，key为songId，value为缓存状态
   */
  async batchCheckVideoCache(userId, songIds) {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法批量检查缓存:', error);
      return new Map();
    }
    
    try {
      console.log('🎬 [视频缓存] 开始批量检查缓存，歌曲数量:', songIds.length);
      
      // 先快速获取用户所有缓存歌曲ID
      const cachedSongIds = await this.getCachedSongIds(userId);
      
      // 批量检查结果
      const results = new Map();
      
      for (const songId of songIds) {
        if (cachedSongIds.has(songId)) {
          results.set(songId, true);
        } else {
          results.set(songId, false);
        }
      }
      
      console.log('🎬 [视频缓存] 批量检查完成，找到缓存:', Array.from(results.values()).filter(Boolean).length, '个');
      return results;
      
    } catch (error) {
      console.error('❌ [视频缓存] 批量检查缓存失败:', error);
      return new Map();
    }
  }

  /**
   * 重置数据库配置（用于解决配置问题）
   */
  async resetDatabase() {
    try {
      console.log('🔄 [视频缓存] 开始重置数据库配置...');
      
      // 关闭当前连接
      if (this.db) {
        this.db.close();
        this.db = null;
        this.isInitialized = false;
        this.initPromise = null;
      }
      
      // 删除数据库
      const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
      
      return new Promise((resolve, reject) => {
        deleteRequest.onsuccess = () => {
          console.log('✅ [视频缓存] 数据库已删除，重新初始化...');
          this.initDB().then(() => {
            console.log('✅ [视频缓存] 数据库重置完成');
            resolve();
          }).catch(reject);
        };
        
        deleteRequest.onerror = () => {
          console.error('❌ [视频缓存] 删除数据库失败:', deleteRequest.error);
          reject(deleteRequest.error);
        };
      });
    } catch (error) {
      console.error('❌ [视频缓存] 重置数据库失败:', error);
      throw error;
    }
  }

  /**
   * 获取所有缓存键
   */
  async getAllCacheKeys() {
    try {
      await this.ensureDBReady();
    } catch (error) {
      console.error('❌ [视频缓存] 数据库初始化失败，无法获取缓存键列表:', error);
      return [];
    }
    
    try {
      const fullCacheKeys = await this.db.getAllKeys(VIDEO_CACHE_STORE);
      const partialCacheKeys = await this.db.getAllKeys(PARTIAL_CACHE_STORE);
      
      return {
        fullCache: fullCacheKeys,
        partialCache: partialCacheKeys,
        all: [...fullCacheKeys, ...partialCacheKeys]
      };
    } catch (error) {
      console.error('❌ [视频缓存] 获取所有缓存键失败:', error);
      return [];
    }
  }
}

// 创建单例实例
const videoCacheService = new VideoCacheService();

export default videoCacheService;
